import {
  Button,
  Card,
  CardContent,
  CardTitle,
  Typography,
} from "@/components/ui";
import { cn } from "@/lib/utils/cn";
import { ChevronDown, Pencil } from "lucide-react";
import { useState } from "react";

export const MatterSectionCardLineItem = ({
  title,
  value,
  valueClassName,
  className,
}: {
  title: string;
  value?: string | React.ReactNode;
  valueClassName?: string;
  className?: string;
}) => {
  return (
    <div className={cn("bg-card py-3", className)}>
      <Typography size="sm" variant="subHeading">
        {title}
      </Typography>
      <Typography className={valueClassName} size="md">
        {value ?? "-"}
      </Typography>
    </div>
  );
};

interface MatterSectionCardProps {
  cardClassName?: string;
  children: React.ReactNode;
  className?: string;
  collapsible?: boolean;
  hasUpdatePermission?: boolean;
  onUpdateClick?: () => void;
  leftButtons?: React.ReactNode;
  rightButtons?: React.ReactNode;
  title: string | React.ReactNode;
  noHeaderBackground?: boolean;
  noBorder?: boolean;
}

export const MatterSectionCard = ({
  cardClassName,
  className,
  collapsible,
  hasUpdatePermission,
  onUpdateClick,
  leftButtons,
  rightButtons,
  title,
  noHeaderBackground = false,
  noBorder = false,
  ...props
}: MatterSectionCardProps) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  return (
    <Card
      className={cn(
        cardClassName,
        "border-0",
        noBorder && "rounded-none shadow-none",
      )}
    >
      <CardTitle
        className={cn(
          "flex justify-between px-4",
          noBorder ? "py-2" : "py-3",
          !noHeaderBackground && "bg-light-primary",
        )}
      >
        <div className="flex">
          {collapsible ? (
            <Button
              onClick={() => {
                setIsCollapsed((prev) => !prev);
              }}
              variant="link"
              className="text-dark-primary h-6 pl-0"
            >
              <Typography size="md" weight="semibold">
                {title}
              </Typography>
              <ChevronDown
                className={cn(
                  "transition-transform duration-200",
                  !isCollapsed && "-rotate-180",
                )}
              />
            </Button>
          ) : (
            <Typography color="primary" size="md" weight="semibold">
              {title}
            </Typography>
          )}
          {leftButtons}
        </div>
        <div
          className="flex"
          data-testid={`${
            typeof title === "string" ? title.toLowerCase() : "section"
          }-edit-button`}
        >
          {rightButtons}
          {hasUpdatePermission && (collapsible ? !isCollapsed : true) && (
            <Button
              onClick={onUpdateClick}
              variant="circleIcon"
              size="icon"
              className="h-8 w-8"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardTitle>
      {collapsible && isCollapsed ? null : (
        <div className={cn(!noBorder && "border-border rounded-md border")}>
          <CardContent
            className={cn(noBorder ? "px-4 py-4" : "px-5 py-6", className)}
          >
            {props.children}
          </CardContent>
        </div>
      )}
    </Card>
  );
};
